/**
 * @file DynamicListLayout.tsx
 * @module app/custom/layouts/DynamicListLayout
 * @description Dynamic list layout with configurable columns.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.1.1
 */

import React from "react";
import { Row, Col, Tab, Card } from "react-bootstrap";
import { CustomNavTabs } from "../nav-tab";
import Pagination from "../../../Global/pagination";

type TabItem = {
    label: string;
    value: string | number;
    count?: number;
};

type DynamicListLayoutProps = {
    title: string;
    action?: React.ReactNode;
    tabs?: TabItem[];
    activeTab?: string | number;
    onTabChange?: (value: string | number) => void;
    filters?: React.ReactNode;
    secondaryAction?: React.ReactNode;
    children: React.ReactNode;

    /** NEW — variant control */
    variant?: "withTabs" | "withoutTabs";

    /** NEW — Pagination Props */   
    totalRecords?: number;
    page?: number | any;
    pageLimit?: number | any;
    handleCurrentPage?: (value: number) => void;
    handleNextPage?: (value: number) => void;
    handlePreviousPage?: (value: number) => void;
    handlePageLimit?: (value: number) => void;
};

const DynamicListLayout: React.FC<DynamicListLayoutProps> = ({
    title,
    action,
    tabs = [],
    activeTab,
    onTabChange,
    filters,
    secondaryAction,
    children,
    variant = "withTabs",
    totalRecords = 0,
    page = 1,
    pageLimit = 10,
    handleCurrentPage,
    handleNextPage,
    handlePreviousPage,
    handlePageLimit,
}) => {
    const showTabs = variant === "withTabs" && tabs.length > 0;

    return (
        <div className="p-5">
            {/* ---------- TITLE & ACTION BUTTON ---------- */}
            <Row className="align-items-start mb-5">
                <Col xs>
                    <h1 className="fs-22 fw-bolder mb-0">{title}</h1>
                </Col>

                {action && <Col xs="auto">{action}</Col>}
            </Row>

            {secondaryAction && (
                <Row className="mb-5">
                    <Col xs={12}>{secondaryAction}</Col>
                </Row>
            )}

            {/* ---------- TABS + FILTERS SECTION ---------- */}
            {showTabs && (
                <Row>
                    <Col xs={12} className="custom-tabContainer mb-8">
                        <Tab.Container activeKey={activeTab}>
                            <div className="variant-categories align-items-center d-flex justify-content-between gap-4 flex-wrap mb-6">
                                {/* LEFT – TABS */}
                                <div className="mb-lg-0 mb-5">
                                    <CustomNavTabs
                                        tabs={tabs}
                                        activeTab={activeTab ?? 1}
                                        onChange={onTabChange ?? (() => { })}
                                    />
                                </div>
                                {/* RIGHT – FILTERS */}
                                {filters}
                            </div>

                            <Tab.Content>
                                <Tab.Pane eventKey={activeTab} active={true}>
                                    <Card className="border border-r10px mt-7">
                                        <Card.Body className="p-0">{children}</Card.Body>
                                    </Card>
                                    <Pagination
                                        totalRecords={totalRecords}
                                        currentPage={page}
                                        handleCurrentPage={handleCurrentPage ?? (() => { })}
                                        handleNextPage={handleNextPage ?? (() => { })}
                                        handlePreviousPage={handlePreviousPage ?? (() => { })}
                                        pageLimit={pageLimit}
                                        handlePageLimit={handlePageLimit ?? (() => { })}
                                    />
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                    </Col>
                </Row>
            )}

            {/* ---------- CONTENT WITHOUT TABS ---------- */}
            {!showTabs && (
                <>
                    {filters}
                    <Card className="border border-r10px mt-7">
                        <Card.Body className="p-0">{children}</Card.Body>
                    </Card>
                    <Pagination
                        totalRecords={totalRecords}
                        currentPage={page}
                        handleCurrentPage={handleCurrentPage ?? (() => { })}
                        handleNextPage={handleNextPage ?? (() => { })}
                        handlePreviousPage={handlePreviousPage ?? (() => { })}
                        pageLimit={pageLimit}
                        handlePageLimit={handlePageLimit ?? (() => { })}
                    />
                </>
            )}
        </div>
    );
};

export default DynamicListLayout;
